import React, { useEffect, useState } from "react";
import { ChevronRight } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";

import Card from "./card";

import "./banner.css";
import "./card.css";

import Map from "../../stdcouncil/map/map";

import Homeimage from "../../../assets/img/HomeImage.png";
import councilDefaultLogo from "../../../assets/img/DS.svg";
import { publicApiClient, getFileUrl } from "../../../api/apiClient";

const BANNER_API = "/api/contact/banner";
const NOTICE_API = "/api/activity/notice?page=0&size=100";
const EVENT_API = "/api/activity/event?page=0&size=100";
const COUNCIL_INTRO_API = "/api/council/intro";

const isVideoUrl = (url = "") => {
  const cleanUrl = String(url || "").split("?")[0].toLowerCase();

  return (
    cleanUrl.endsWith(".mp4") ||
    cleanUrl.endsWith(".mov") ||
    cleanUrl.endsWith(".webm") ||
    cleanUrl.endsWith(".ogg")
  );
};

const getArrayData = (result) => {
  const data = result?.data;

  if (Array.isArray(data)) return data;

  if (Array.isArray(data?.content)) return data.content;
  if (Array.isArray(data?.contents)) return data.contents;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.list)) return data.list;

  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.data?.content)) return data.data.content;

  return [];
};

const normalizeBanner = (item) => {
  const mediaUrl =
    item.mediaUrl ||
    item.media_url ||
    item.imageUrl ||
    item.bannerImageUrl ||
    item.mainImgUrl ||
    item.mainImg ||
    item.imgUrl ||
    item.videoUrl ||
    item.fileUrl ||
    item.url ||
    "";

  const fullUrl = getFileUrl(mediaUrl);

  if (!fullUrl) return null;

  return {
    id: item.mainBannerId || item.bannerId || item.id || fullUrl,
    url: fullUrl,
    type: isVideoUrl(mediaUrl) || isVideoUrl(fullUrl) ? "video" : "image",
  };
};

const normalizeNotice = (item) => ({
  id: item.noticeId || item.notice_id || item.id,
  title: item.title || "",
  writer:
    item.writer ||
    item.createdByName ||
    item.createdBy ||
    item.author ||
    "관리자",
  date:
    item.noticeDate ||
    item.date ||
    item.createdAt ||
    item.created_at ||
    item.updatedAt ||
    item.updated_at ||
    "",
  createdAt: item.createdAt || item.created_at || "",
  updatedAt: item.updatedAt || item.updated_at || "",
});

const normalizeEvent = (item) => {
  const imageUrl =
    item.mainImg ||
    item.mainImgUrl ||
    item.mainImageUrl ||
    item.thumbnailUrl ||
    item.imageUrl ||
    item.eventImageUrl ||
    item.fileUrl ||
    item.imgUrl ||
    "";

  return {
    id: item.eventId || item.event_id || item.id,
    title: item.title || item.eventName || "",
    date:
      item.eventDate ||
      item.date ||
      item.createdAt ||
      item.created_at ||
      item.startDate ||
      "",
    createdAt: item.createdAt || item.created_at || "",
    updatedAt: item.updatedAt || item.updated_at || "",
    startDate: item.startDate || item.start_date || "",
    imageUrl: getFileUrl(imageUrl),
  };
};

const getTimeValue = (value) => {
  if (!value) return 0;

  const date = new Date(value);

  if (!Number.isNaN(date.getTime())) {
    return date.getTime();
  }

  const normalized = String(value).replaceAll(".", "-");
  const normalizedDate = new Date(normalized);

  if (!Number.isNaN(normalizedDate.getTime())) {
    return normalizedDate.getTime();
  }

  return 0;
};

const getLatestFour = (list = []) => {
  return [...list]
    .sort((a, b) => {
      const aTime = Math.max(
        getTimeValue(a.createdAt),
        getTimeValue(a.updatedAt),
        getTimeValue(a.date),
        getTimeValue(a.startDate)
      );

      const bTime = Math.max(
        getTimeValue(b.createdAt),
        getTimeValue(b.updatedAt),
        getTimeValue(b.date),
        getTimeValue(b.startDate)
      );

      return bTime - aTime;
    })
    .slice(0, 4);
};

const formatDate = (value) => {
  if (!value) return "";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return String(value).slice(0, 10).replaceAll("-", ".");
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}.${month}.${day}`;
};

export default function Main() {
  const navigate = useNavigate();

  const [currentBanner, setCurrentBanner] = useState(0);

  const [banners, setBanners] = useState([]);
  const [notices, setNotices] = useState([]);
  const [events, setEvents] = useState([]);
  const [councilLogo, setCouncilLogo] = useState(councilDefaultLogo);

  const [loading, setLoading] = useState(false);

  const handleEventClick = (eventId) => {
    console.log("클릭한 행사 id:", eventId);

    if (!eventId) {
      alert("행사 정보를 찾을 수 없습니다.");
      return;
    }

    navigate("/stdcouncil-log/Event", {
      state: {
        openEventId: eventId,
      },
    });
  };

  useEffect(() => {
    const fetchMainData = async () => {
      try {
        setLoading(true);

        const [bannerResult, noticeResult, eventResult, introResult] =
          await Promise.allSettled([
            publicApiClient(BANNER_API),
            publicApiClient(NOTICE_API),
            publicApiClient(EVENT_API),
            publicApiClient(COUNCIL_INTRO_API),
          ]);

        if (bannerResult.status === "fulfilled") {
          const bannerData = getArrayData(bannerResult.value)
            .map(normalizeBanner)
            .filter(Boolean);

          setBanners(
            bannerData.length > 0
              ? bannerData
              : [
                  {
                    id: "default-banner",
                    url: Homeimage,
                    type: "image",
                  },
                ]
          );

          setCurrentBanner(0);
        } else {
          console.error("메인 배너 조회 실패:", bannerResult.reason);

          setBanners([
            {
              id: "default-banner",
              url: Homeimage,
              type: "image",
            },
          ]);

          setCurrentBanner(0);
        }

        if (noticeResult.status === "fulfilled") {
          const noticeData = getLatestFour(
            getArrayData(noticeResult.value)
              .map(normalizeNotice)
              .filter((notice) => notice.id)
          );

          setNotices(noticeData);
        } else {
          console.error("공지사항 조회 실패:", noticeResult.reason);
          setNotices([]);
        }

        if (eventResult.status === "fulfilled") {
          const eventData = getLatestFour(
            getArrayData(eventResult.value)
              .map(normalizeEvent)
              .filter((event) => event.id)
          );

          console.log("메인 행사 데이터:", eventData);
          setEvents(eventData);
        } else {
          console.error("행사 조회 실패:", eventResult.reason);
          setEvents([]);
        }

        if (introResult.status === "fulfilled") {
          const intro = introResult.value?.data;

          const logoUrl =
            intro?.mainImgUrl ||
            intro?.mainImageUrl ||
            intro?.logoImageUrl ||
            intro?.mainImg ||
            "";

          setCouncilLogo(logoUrl ? getFileUrl(logoUrl) : councilDefaultLogo);
        } else {
          console.error("총학생회 로고 조회 실패:", introResult.reason);
          setCouncilLogo(councilDefaultLogo);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchMainData();
  }, []);

  const safeBanners =
    banners.length > 0
      ? banners
      : [
          {
            id: "default-banner",
            url: Homeimage,
            type: "image",
          },
        ];

  const currentBannerItem = safeBanners[currentBanner];

  const next = () => {
    setCurrentBanner((prev) => (prev + 1) % safeBanners.length);
  };

  const prev = () => {
    setCurrentBanner(
      (prev) => (prev - 1 + safeBanners.length) % safeBanners.length
    );
  };

  const renderMainBanner = () => {
    if (!currentBannerItem?.url) return null;

    if (currentBannerItem.type === "video") {
      return (
        <div className="banner-video-frame">
          <video
            src={currentBannerItem.url}
            className="banner-video"
            autoPlay
            muted
            loop
            playsInline
            controls={false}
          />
        </div>
      );
    }

    return (
      <img
        src={currentBannerItem.url}
        alt={`배너 ${currentBanner + 1}`}
        className="banner-img"
      />
    );
  };

  return (
    <div>
      <section className="banner-wrap full-bleed">
        {renderMainBanner()}

        {safeBanners.length > 1 && (
          <>
            <div className="banner-nav">
              <button className="banner-btn" onClick={prev} type="button">
                ‹
              </button>

              <button className="banner-btn" onClick={next} type="button">
                ›
              </button>
            </div>

            <div className="banner-dots">
              {safeBanners.map((banner, i) => (
                <div
                  key={banner.id || i}
                  className={`banner-dot ${
                    i === currentBanner ? "active" : ""
                  }`}
                />
              ))}
            </div>
          </>
        )}
      </section>

      <section className="cards-row">
        <div className="cards-col">
          <Card
            title="공지사항"
            to="/stdcouncil-log/notice"
            bodyClassName="main-card-body-inner"
          >
            {loading ? (
              <p className="notice-empty">공지사항을 불러오는 중입니다.</p>
            ) : notices.length > 0 ? (
              <ul className="notice-list">
                {notices.map((notice) => (
                  <li key={notice.id} className="notice-item">
                    <Link
                      to={`/stdcouncil-log/notice/${notice.id}`}
                      className="notice-link"
                    >
                      <div className="notice-logo">
                        <img
                          src={councilLogo || councilDefaultLogo}
                          alt="총학생회 로고"
                          onError={(e) => {
                            e.currentTarget.onerror = null;
                            e.currentTarget.src = councilDefaultLogo;
                          }}
                        />
                      </div>

                      <div className="notice-content">
                        <p className="notice-title">{notice.title}</p>
                        <p className="notice-desc">{notice.writer}</p>
                      </div>

                      <span className="notice-date">
                        {formatDate(notice.date)}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="notice-empty">등록된 공지사항이 없습니다.</p>
            )}
          </Card>
        </div>

        <div className="cards-col">
          <Card
            title="행사"
            to="/stdcouncil-log/Event"
            bodyClassName="main-card-body-inner"
          >
            {loading ? (
              <p className="notice-empty">행사를 불러오는 중입니다.</p>
            ) : events.length > 0 ? (
              <div className="event-grid">
                {events.map((event) => (
                  <button
                    key={event.id}
                    type="button"
                    className="event-thumb"
                    title={event.title}
                    onClick={() => handleEventClick(event.id)}
                  >
                    {event.imageUrl ? (
                      <img
                        src={event.imageUrl}
                        alt={event.title || "행사"}
                        className="event-thumb-img"
                      />
                    ) : (
                      <span>{event.title || "행사"}</span>
                    )}
                  </button>
                ))}
              </div>
            ) : (
              <p className="notice-empty">등록된 행사가 없습니다.</p>
            )}
          </Card>
        </div>
      </section>

      <Map showTab={false} variant="main" />

      <section className="main-inquiry-section">
        <div className="main-inquiry-inner">
          <h3 className="main-inquiry-title">궁금한 점이 더 있다면?</h3>

          <p className="main-inquiry-desc">
            총학생회에게 무엇이든 물어보세요!
          </p>

          <div className="main-inquiry-grid">
            <Link to="/ask" className="main-inquiry-card">
              <div className="main-inquiry-text">
                <p>총학생회에 직접 문의하고 싶다면</p>
                <h4>소통창구 확인하러 가기</h4>
              </div>

              <div className="main-inquiry-icon">
                <ChevronRight size={22} />
              </div>
            </Link>

            <Link to="/faq" className="main-inquiry-card">
              <div className="main-inquiry-text">
                <p>자주 묻는 질문을 모아두었어요</p>
                <h4>FAQ 바로가기</h4>
              </div>

              <div className="main-inquiry-icon">
                <ChevronRight size={22} />
              </div>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}